import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowSquareOutIcon, CircleNotchIcon } from '@phosphor-icons/react';
import { BaseModal } from '../ui/BaseModal';
import {
  useGetDocumentPresignedUrl,
  useRejectDocument,
  useValidateDocument,
} from '../../services/documents.service';

interface TaskValidationModalProps {
  companyId: string;
}

export interface TaskValidationModalRef {
  open: (documentId: string, documentName: string, nodeId: string | null) => void;
}

export const TaskValidationModal = forwardRef<TaskValidationModalRef, TaskValidationModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [documentName, setDocumentName] = useState('');
    const [nodeId, setNodeId] = useState<string | null>(null);
    const [comment, setComment] = useState('');

    const validateMutation = useValidateDocument(companyId);
    const rejectMutation = useRejectDocument(companyId);
    const presignedUrlMutation = useGetDocumentPresignedUrl(companyId);

    useImperativeHandle(ref, () => ({
      open: (docId: string, docName: string, docNodeId: string | null) => {
        setDocumentId(docId);
        setDocumentName(docName);
        setNodeId(docNodeId);
        setComment('');
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        setDocumentId(null);
        setDocumentName('');
        setNodeId(null);
        setComment('');
      }
    }, [isOpen]);

    const handleViewDocument = () => {
      if (!nodeId) return;
      presignedUrlMutation.mutate(nodeId, {
        onSuccess: (result) => window.open(result.url, '_blank', 'noopener,noreferrer'),
      });
    };

    const handleClose = () => setIsOpen(false);

    const handleValidate = () => {
      if (!documentId) return;
      validateMutation.mutate(
        { documentId, ...(comment ? { comment } : {}) },
        { onSuccess: handleClose },
      );
    };

    const handleReject = () => {
      if (!documentId || !comment) return;
      rejectMutation.mutate({ documentId, reason: comment }, { onSuccess: handleClose });
    };

    const isLoading = validateMutation.isPending || rejectMutation.isPending;

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleReject}
          disabled={isLoading || !comment}
          className="flex-1 px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {rejectMutation.isPending && (
            <CircleNotchIcon size={16} className="animate-spin" weight="bold" />
          )}
          {t('tasks.validation_modal.reject')}
        </button>
        <button
          type="button"
          onClick={handleValidate}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {validateMutation.isPending && (
            <CircleNotchIcon size={16} className="animate-spin" weight="bold" />
          )}
          {t('tasks.validation_modal.validate')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('tasks.validation_modal.title')}
        footer={footer}
        maxWidth="md"
      >
        <div className="flex flex-col gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('tasks.validation_modal.document_name')}
            </label>
            <p className="text-gray-900 font-medium">{documentName}</p>
          </div>

          <button
            type="button"
            onClick={handleViewDocument}
            disabled={!nodeId || presignedUrlMutation.isPending}
            className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {presignedUrlMutation.isPending ? (
              <CircleNotchIcon size={16} className="animate-spin" weight="bold" />
            ) : (
              <ArrowSquareOutIcon size={16} />
            )}
            {t('tasks.validation_modal.view_document')}
          </button>

          <div>
            <label
              htmlFor="task-validation-comment"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('tasks.validation_modal.comment_label')}
            </label>
            <textarea
              id="task-validation-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder={t('tasks.validation_modal.comment_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
      </BaseModal>
    );
  },
);

TaskValidationModal.displayName = 'TaskValidationModal';
